import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { updateWill, updateWillWithMedia } from '../services/willService';
import { toast } from "sonner";
import NavBar from '../components/NavBar';
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import RichTextEditor from '../components/RichTextEditor';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Smile, Edit, Check } from "lucide-react";
import { useLanguage } from '../contexts/LanguageContext';
import MediaUploadForWill from '@/components/MediaUploadForWill';
import { Categories, getMainCategories, getSubcategories, CategoryType } from "../types/category";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { v4 as uuidv4 } from 'uuid';
import '../components/ReviewStyles.css'; // Import the review styles

const EditWill = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const will = location.state?.will;
  const { language } = useLanguage();
  const isRTL = language === 'he';
  const mainCategories = getMainCategories();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const descriptionEditorRef = useRef<any>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define the processExistingMedia function before using it
  const processExistingMedia = (mediaItems: any[] = []) => {
    console.log("Processing media items input:", mediaItems);
    
    if (!mediaItems || !Array.isArray(mediaItems) || mediaItems.length === 0) {
      console.log("No media items to process");
      return [];
    }
    
    // Try to handle different possible media formats
    const processedMedia = mediaItems.map(item => {
      console.log("Processing media item:", item);
      
      // Ensure we have an ID
      const id = item.id || uuidv4();
      
      // Ensure we have a URL - try different possible properties
      let previewUrl = item.url || item.previewUrl;
      if (!previewUrl) {
        console.error("Media item has no URL:", item);
        return null;
      }
      
      // Force HTTPS for S3 URLs if they're using HTTP
      if (previewUrl.includes('amazonaws.com') && previewUrl.startsWith('http:')) {
        previewUrl = previewUrl.replace('http:', 'https:');
      }
      
      // Add timestamp to URL to prevent caching issues
      if (previewUrl.includes('?')) {
        previewUrl = `${previewUrl}&t=${Date.now()}`;
      } else {
        previewUrl = `${previewUrl}?t=${Date.now()}`;
      }
      
      console.log(`Processed media item ${id}, URL: ${previewUrl}`);
      
      return {
        id: id,
        file: new File([], item.name || 'image.jpg', { type: item.mimeType || 'image/jpeg' }),
        previewUrl: previewUrl,
        uploaded: true,
        uploading: false
      };
    }).filter(Boolean); // Remove any null items
    
    console.log("Final processed media:", processedMedia);
    return processedMedia;
  };
  
  // Now we can use the function
  const [media, setMedia] = useState<any[]>(processExistingMedia(will?.media));

  useEffect(() => {
    if (!will) {
      navigate('/');
    }
  }, [will, navigate]);

  useEffect(() => {
    console.log("Will object received in EditWill:", will);
    console.log("Will media received:", will?.media);
  }, [will]);

  const [formData, setFormData] = useState({
    title: will?.title || '',
    description: will?.description || '',
    budget: will?.budget || '',
    currency: will?.currency || 'ILS',
    fulfillmentTime: will?.fulfillmentTime?.split('T')[0] || '',
    fulfillmentTimeType: will?.fulfillmentTimeType || 'SPECIFIC_DATE',
    recurrencePattern: will?.recurrencePattern || 'NONE',
    category: will?.category || '',
    location: will?.location || '',
    budgetType: will?.budget === 0 ? 'FREE' : 
               (will?.budget > 0 ? 'FIXED' : 'OPEN')
  });

  // Toggle edit mode for a section
  const toggleEditSection = (section: string) => {
    setEditingSection(editingSection === section ? null : section);
  };

  // Format the time/date for display
  const formatTime = () => {
    if (formData.fulfillmentTimeType === 'ASAP') return 'As soon as possible';
    if (formData.fulfillmentTimeType === 'THIS_WEEK') return 'This week';
    if (formData.fulfillmentTimeType === 'SPECIFIC_DATE' && formData.fulfillmentTime) {
      return new Date(formData.fulfillmentTime).toLocaleDateString();
    }
    return '';
  };

  // Format the budget for display
  const formatBudget = () => {
    if (formData.budgetType === 'OPEN') return 'Open to offers';
    if (formData.budgetType === 'FREE') return 'FREE';
    if (formData.budgetType === 'FIXED' && formData.budget) {
      return `${formData.budget} ${formData.currency}`;
    }
    return '';
  };

  // Check if date is required but missing
  const isDateRequired = () => {
    return formData.fulfillmentTimeType === 'SPECIFIC_DATE' && !formData.fulfillmentTime;
  };

  // Find the category display name from the selected category value
  const getCategoryDisplayName = (categoryValue: string): string => {
    // First check main categories
    const mainCategory = mainCategories.find(cat => cat.value === categoryValue);
    if (mainCategory) return mainCategory.displayName;
    
    // Then check subcategories
    for (const mainCat of mainCategories) {
      const subcategories = getSubcategories(mainCat.value);
      const subcat = subcategories.find(sub => sub.value === categoryValue);
      if (subcat) return `${mainCat.displayName} > ${subcat.displayName}`;
    }
    
    return categoryValue; // Fallback to the value if not found
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Don't allow submission if any section is currently being edited
    if (editingSection) {
      toast.error("Please finish editing all sections before submitting");
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log('Form submission started');
      
      const formattedDate = formData.fulfillmentTime 
        ? `${formData.fulfillmentTime}T12:00:00` 
        : undefined;

      const submitData = {
        ...formData,
        budget: formData.budgetType === 'FIXED' ? Number(formData.budget) : 
                formData.budgetType === 'FREE' ? 0 : null,
        currency: formData.budgetType === 'FIXED' ? formData.currency : null,
        fulfillmentTime: formattedDate,
        location: formData.location || undefined
      };

      console.log('Submitting data:', submitData);

      // Get new files to upload - ensure they are valid File objects
      const mediaFiles = media
        .filter(item => item.file && !item.uploaded && item.file instanceof File && item.file.size > 0)
        .map(item => item.file);
      
      console.log('Media files to upload:', mediaFiles);
      
      // Get IDs of existing media to keep
      const existingMediaIds = media
        .filter(item => item.uploaded)
        .map(item => item.id);
      
      console.log('Media to keep (existingMediaIds):', existingMediaIds);
      
      // Add media information to the submit data
      const dataWithMedia = {
        ...submitData,
        existingMediaIds
      };
      
      let result;
      
      if (mediaFiles.length > 0) {
        console.log('Updating will with new media files');
        result = await updateWillWithMedia(id!, dataWithMedia, mediaFiles);
      } else {
        console.log('Updating will without new media files');
        result = await updateWill(id!, dataWithMedia);
      }

      console.log('Update result:', result);
      toast.success("Want updated successfully");
      navigate(`/will/${id}`);
    } catch (error) {
      console.error('Failed to update want:', error);
      toast.error(`Failed to update want: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDescriptionChange = (html: string) => {
    setFormData(prev => ({
      ...prev,
      description: html
    }));
  };
  
  const handleMediaChange = (newMedia: any[]) => {
    console.log('Media changed:', newMedia);
    setMedia(newMedia);
  };

  const handleCategoryChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      category: value
    }));
  };

  const addEmoji = (emoji: any) => {
    if (descriptionEditorRef.current) {
      descriptionEditorRef.current.insertText(emoji.native);
    }
    setShowEmojiPicker(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <main className="container mx-auto px-4 pt-16 pb-12">
        <div className="max-w-2xl mx-auto">
          <Link 
            to={`/will/${id}`}
            className={`inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm transition-colors ${isRTL ? "flex-row-reverse" : ""}`}
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? "ml-1 mirror-in-rtl" : "mr-1"}`} />
            {isRTL ? "חזור לרצון" : "Back to Want"}
          </Link>

          <h1 className="text-2xl font-semibold mb-6">{isRTL ? "ערוך רצון" : "Edit Want"}</h1>
          
          <form 
            onSubmit={handleSubmit} 
            className="space-y-6" 
            dir={isRTL ? "rtl" : "ltr"}
            encType="multipart/form-data"
          >
            <div className="review-container">
              <div className="review-card">
                <div className="review-header">
                  <h4 className="review-title">{formData.title}</h4>
                  {formData.category && (
                    <div className="review-category">
                      {getCategoryDisplayName(formData.category)}
                    </div>
                  )}
                </div>

                {/* Title Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Title</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('title')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'title' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'title' ? (
                    <div className="edit-form-field">
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                        required
                        placeholder="Title"
                      />
                    </div>
                  ) : (
                    <div className="review-content">{formData.title}</div>
                  )}
                </div>
                
                {/* Description Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Description</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('description')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'description' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'description' ? (
                    <div className="edit-form-field">
                      <div className="relative">
                        <RichTextEditor
                          ref={descriptionEditorRef}
                          value={formData.description}
                          onChange={handleDescriptionChange}
                          minHeight="150px"
                          required
                        />
                        <div className="absolute bottom-2 right-2">
                          <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                              >
                                <Smile className="h-5 w-5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 border-none shadow-xl" align="end">
                              <Picker 
                                data={data} 
                                onEmojiSelect={addEmoji}
                                theme="light"
                                previewPosition="none"
                                skinTonePosition="none"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="review-content" dangerouslySetInnerHTML={{ __html: formData.description }}></div>
                  )}
                </div>
                
                {/* Category Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Category</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('category')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'category' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'category' ? (
                    <div className="edit-form-field">
                      <Select
                        value={formData.category}
                        onValueChange={handleCategoryChange}
                        required
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border rounded-lg shadow-lg max-h-[300px] overflow-auto">
                          {mainCategories.map((mainCategory: CategoryType, index) => {
                            const subCategories = getSubcategories(mainCategory.value);
                            
                            return (
                              <div key={mainCategory.value}>
                                {index > 0 && <div className="h-px bg-gray-200 my-2 mx-2"></div>}
                                <SelectGroup>
                                  <SelectLabel className="font-bold text-gray-900 py-2 px-3 bg-gray-50 rounded-md my-1 mx-1.5 block">
                                    {mainCategory.displayName}
                                  </SelectLabel>
                                  <SelectItem 
                                    key={mainCategory.value} 
                                    value={mainCategory.value}
                                    className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-4 border-l-2 border-transparent hover:border-[#222222] ml-3 text-gray-700"
                                  >
                                    {mainCategory.displayName}
                                  </SelectItem>
                                  {subCategories.map((subCategory: CategoryType) => (
                                    <SelectItem 
                                      key={subCategory.value} 
                                      value={subCategory.value}
                                      className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-8 ml-4 text-gray-600 text-sm border-l-2 border-transparent hover:border-[#222222]"
                                    >
                                      {subCategory.displayName}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </div>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="review-content">{getCategoryDisplayName(formData.category)}</div>
                  )}
                </div>
                
                {/* Time & Location Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Time & Location</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('time-location')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'time-location' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'time-location' ? (
                    <div className="edit-form-field">
                      <div className="edit-field-group">
                        <label className="edit-label">When do you want it?</label>
                        <div className="radio-group">
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-asap"
                              name="edit-fulfillmentTimeType"
                              value="ASAP"
                              checked={formData.fulfillmentTimeType === 'ASAP'}
                              onChange={() => setFormData(prev => ({...prev, fulfillmentTimeType: 'ASAP'}))}
                            />
                            <label htmlFor="edit-asap">Now (ASAP)</label>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-this_week"
                              name="edit-fulfillmentTimeType"
                              value="THIS_WEEK"
                              checked={formData.fulfillmentTimeType === 'THIS_WEEK'}
                              onChange={() => setFormData(prev => ({...prev, fulfillmentTimeType: 'THIS_WEEK'}))}
                            />
                            <label htmlFor="edit-this_week">This week</label>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-specific_date"
                              name="edit-fulfillmentTimeType"
                              value="SPECIFIC_DATE"
                              checked={formData.fulfillmentTimeType === 'SPECIFIC_DATE'}
                              onChange={() => setFormData(prev => ({...prev, fulfillmentTimeType: 'SPECIFIC_DATE'}))}
                            />
                            <label htmlFor="edit-specific_date">Pick a date</label>
                          </div>
                        </div>
                        
                        {formData.fulfillmentTimeType === 'SPECIFIC_DATE' && (
                          <div className="mt-2">
                            <input
                              type="date"
                              value={formData.fulfillmentTime}
                              onChange={(e) => setFormData(prev => ({...prev, fulfillmentTime: e.target.value}))}
                              className={`date-picker w-full ${isDateRequired() ? 'border-red-500' : ''}`}
                              required
                            />
                            {isDateRequired() && (
                              <p className="text-red-500 text-xs mt-1">Please select a date</p>
                            )}
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <div className="checkbox-group">
                            <input
                              type="checkbox"
                              id="edit-is_recurring"
                              checked={formData.recurrencePattern !== 'NONE'}
                              onChange={(e) => setFormData(prev => ({
                                ...prev, 
                                recurrencePattern: e.target.checked ? 'DAILY' : 'NONE'
                              }))}
                            />
                            <label htmlFor="edit-is_recurring">Recurring</label>
                          </div>
                          
                          {formData.recurrencePattern !== 'NONE' && (
                            <div className="select-group mt-2">
                              <select
                                value={formData.recurrencePattern}
                                onChange={(e) => setFormData(prev => ({...prev, recurrencePattern: e.target.value}))}
                                className="w-full"
                              >
                                <option value="DAILY">Daily</option>
                                <option value="WEEKLY">Weekly</option>
                                <option value="MONTHLY">Monthly</option>
                                <option value="YEARLY">Yearly</option>
                                <option value="CUSTOM">Custom</option>
                              </select>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4">
                          <label className="edit-label" htmlFor="edit-location">Where?</label>
                          <input
                            id="edit-location"
                            type="text"
                            value={formData.location}
                            onChange={(e) => setFormData(prev => ({...prev, location: e.target.value}))}
                            placeholder="City, area, or online"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="review-details">
                      {formatTime() && (
                        <div className="review-detail">
                          <span className="detail-label">When:</span>
                          <span className="detail-value">{formatTime()}</span>
                        </div>
                      )}
                      
                      {isDateRequired() && (
                        <div className="review-detail error-detail">
                          <span className="detail-label">Error:</span>
                          <span className="detail-value text-red-500">Please select a date</span>
                        </div>
                      )}
                      
                      {formData.recurrencePattern !== 'NONE' && (
                        <div className="review-detail">
                          <span className="detail-label">Recurrence:</span>
                          <span className="detail-value">
                            {formData.recurrencePattern.charAt(0) + formData.recurrencePattern.slice(1).toLowerCase()}
                          </span>
                        </div>
                      )}
                      
                      {formData.location && (
                        <div className="review-detail">
                          <span className="detail-label">Where:</span>
                          <span className="detail-value">{formData.location}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Budget Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Budget</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('budget')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'budget' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'budget' ? (
                    <div className="edit-form-field">
                      <div className="edit-field-group">
                        <label className="edit-label">How much are you willing to pay?</label>
                        <div className="radio-group">
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-fixed"
                              name="edit-budgetType"
                              value="FIXED"
                              checked={formData.budgetType === 'FIXED'}
                              onChange={() => setFormData(prev => ({...prev, budgetType: 'FIXED'}))}
                            />
                            <label htmlFor="edit-fixed">Fixed amount</label>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-open"
                              name="edit-budgetType"
                              value="OPEN"
                              checked={formData.budgetType === 'OPEN'}
                              onChange={() => setFormData(prev => ({...prev, budgetType: 'OPEN'}))}
                            />
                            <label htmlFor="edit-open">Open to offers</label>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="edit-free"
                              name="edit-budgetType"
                              value="FREE"
                              checked={formData.budgetType === 'FREE'}
                              onChange={() => setFormData(prev => ({...prev, budgetType: 'FREE'}))}
                            />
                            <label htmlFor="edit-free">FREE</label>
                          </div>
                        </div>
                        
                        {formData.budgetType === 'FIXED' && (
                          <div className="form-row mt-3">
                            <div className="form-group">
                              <input
                                id="edit-budget"
                                type="number"
                                value={formData.budget}
                                onChange={(e) => setFormData(prev => ({...prev, budget: e.target.value}))}
                                required
                                placeholder="Enter your budget"
                                min="0"
                                step="0.01"
                                className="w-full"
                              />
                            </div>
                            
                            <div className="form-group mt-2">
                              <select
                                id="edit-currency"
                                value={formData.currency}
                                onChange={(e) => setFormData(prev => ({...prev, currency: e.target.value}))}
                                className="w-full"
                              >
                                <option value="ILS">ILS</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="review-budget">{formatBudget()}</div>
                  )}
                </div>
                
                {/* Media Section */}
                <div className="review-section">
                  <div className="review-section-header">
                    <h5>Media</h5>
                    <button 
                      type="button" 
                      className="edit-button"
                      onClick={() => toggleEditSection('media')}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingSection === 'media' ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingSection === 'media' ? (
                    <div className="edit-form-field">
                      <MediaUploadForWill
                        existingMedia={media}
                        onMediaChange={handleMediaChange}
                        maxFiles={5}
                      />
                    </div>
                  ) : (
                    <div className="review-media">
                      {media.length > 0 ? (
                        media.map((item, index) => (
                          <div key={index} className="media-preview">
                            {item.previewUrl && (
                              <img 
                                src={item.previewUrl} 
                                alt={`Media ${index + 1}`} 
                                className="media-thumbnail"
                              />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 text-sm italic">No media attached</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between">
              <Link 
                to={`/will/${id}`}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {isRTL ? "ביטול" : "Cancel"}
              </Link>
              
              <button 
                type="submit"
                disabled={
                  isSubmitting || 
                  !formData.title || 
                  !formData.description || 
                  !formData.category || 
                  (formData.budgetType === 'FIXED' && !formData.budget) || 
                  editingSection !== null ||
                  isDateRequired()
                }
                className="px-6 py-2 bg-[#222222] text-white font-medium rounded-lg hover:bg-[#333333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (isRTL ? 'שומר שינויים...' : 'Saving...') : (isRTL ? 'שמור שינויים' : 'Save Changes')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditWill;
